import { FomanticShim, PartialNullable } from "../../Extensions";
import { $$, IJNativeEvent } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyString } from "../../RadObject";
import { WindowShim } from "../../RadWindow";
import { ClientAwareCategoryAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDisplayNameAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { JsxFragment } from "../JsxFragment";
import { ContainerControl, ContainerControlPropertyValues, IContainerControl } from "./ContainerControl";
import { NAMESPACE } from "./_Namespace";

import "./MainMenu.scss";

const
    radWindow = WindowShim();

export enum MainMenuTransitionsEnum
{
    overlay = "overlay",
    push = "push",
    scaleDown = "scale down",
}

export type MainMenuPropertyValues =
    ContainerControlPropertyValues
    & {
        mobileTransition: string;
        showEntityIcon: boolean;
        transition: string;
    };

export type MainMenuProperties = {
    [name in keyof MainMenuPropertyValues]: Property<MainMenuPropertyValues[name]>
};

export interface IMainMenu extends IContainerControl, MainMenuProperties
{
}

export class MainMenu extends ContainerControl implements IMainMenu
{
    public static override classNamespace = NAMESPACE + "MainMenu";
    public static override className = "MainMenu";

    // TODO: BAM
    public start(context?: HTMLElement)
    {
        FomanticShim(this.$$element.get())
            .sidebar({
                context,
                mobileTransition: this.mobileTransition.get(),
                transition: this.transition.get(),
                onVisible: function ()
                {
                    $$("html").addClass("rad-state-main-menu-visible");
                },
                onHide: function ()
                {
                    $$("html").removeClass("rad-state-main-menu-visible");
                }
            });
    }

    // #region Static

    // #region Global Variables

    public static instance?: MainMenu;

    // #endregion

    // #region Constructor

    static {
        $$(() =>
        {
            $$(document).on("click", "a.rad-action-main-menu-open", (evt: IJNativeEvent) =>
            {
                evt.preventDefault();
                evt.stopPropagation();

                MainMenu.instance?.open();
            });
            $$(document).on("click", "a.rad-action-main-menu-close, .rad-control-main-menu a", (evt: IJNativeEvent) =>
            {
                const
                    target = (evt.currentTarget as HTMLElement).getAttribute("target");

                if (target && target !== "_self")
                    return;

                evt.preventDefault();
                evt.stopPropagation();

                MainMenu.instance?.close();
            });
        });
    }

    // #endregion

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<MainMenuPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                mobileTransition: MainMenuTransitionsEnum.overlay,
                showEntityIcon: true,
                transition: MainMenuTransitionsEnum.overlay,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Properties

    public mobileTransition!: PropertyString;
    public showEntityIcon!: PropertyBoolean;
    public transition!: PropertyString;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<MainMenuPropertyValues>)
    {
        super(propertyValues);

        if (MainMenu.instance)
            throw new Error("Only one main menu is supported.");

        MainMenu.instance = this;

        this.addCssClass("rad-control-main-menu ui left vertical menu sidebar");
        //FomanticShim(this.$$element.get())
        //    .sidebar({
        //        mobileTransition: this.mobileTransition.get(),
        //        transition: this.transition.get(),
        //        onVisible: function ()
        //        {
        //            $$("html").addClass("rad-state-main-menu-visible");
        //        },
        //        onHide: function ()
        //        {
        //            $$("html").removeClass("rad-state-main-menu-visible");
        //        }
        //    });

        this._fixChromeRedrawIssue();

        // Trigger Property Changes
        this.mobileTransition.triggerChange();
        this.showEntityIcon.triggerChange();
        this.transition.triggerChange();
    }

    // #endregion

    // #region Public Methods

    public close()
    {
        //this.removeCssClass("rad-state-open");
        FomanticShim(this.$$element.get()).sidebar("hide");
    }
    public open()
    {
        //this.addCssClass("rad-state-open");
        FomanticShim(this.$$element.get()).sidebar("show");
    }

    // #endregion

    // #region Protected Methods

    protected _fixChromeRedrawIssue()
    {
        /* INFO: Don't laugh.
           Don't remove. */
        if (radWindow.chrome && (window.navigator as any).vendor === "Google Inc.")
        {
            let
                b = false,
                firstDiv: any;

            this.on("scroll", () =>
            {
                if (!firstDiv)
                    firstDiv = this.$$element.get()?.find(">div");
                firstDiv.css({ borderWidth: b ? 1 : 0 });
                b = !b;
            });
        }
    }
    protected _mobileTransitionChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _showEntityIconChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.removeCssClass("rad-state-button-icons-hidden");
        else
            this.addCssClass("rad-state-button-icons-hidden");
    }
    protected _transitionChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: MainMenuPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.mobileTransition = new PropertyString({
            context: this,
            initialValue: propertyValues.mobileTransition,
            changeHandler: this._mobileTransitionChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Appearance" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Transition (Mobile)" }),
            ],
        });
        this.showEntityIcon = new PropertyBoolean(this, propertyValues.showEntityIcon, this._showEntityIconChanged);
        this.transition = new PropertyString({
            context: this,
            initialValue: propertyValues.transition,
            changeHandler: this._transitionChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Appearance" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Transition" }),
            ],
        });
    }

    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <nav>
                <div ref={this._containerElementRef}>
                    {children ?? this._createChildrenHtml()}
                </div>
            </nav>
        );
    }

    // #endregion

    // #endregion
}

